import { UNLISTED_PUBLIC_PAGES } from "@shared/unlisted-public-pages";

const UNLISTED_SCREEN_SHARE_PATHS = new Set<string>(
  UNLISTED_PUBLIC_PAGES.map((page) => page.path),
);

/**
 * Whether the sticky cookie consent banner should render.
 *
 * Hidden for authenticated sessions and portal / deal-room shells so the
 * fixed overlay cannot intercept bottom CTAs (e.g. Proceed to NDA). Public
 * anonymous surfaces like /refer and /agents/apply still show it; body
 * padding + z-index keep Create Account clickable there. Preferences remain
 * available via the footer.
 *
 * Path matching uses `/portal` or `/portal/…` (not bare `startsWith("/agent")`)
 * so public `/agents/apply` is not treated as the partner portal.
 *
 * Unlisted walkthroughs (`/overview`, `/energy-briefing`, `/partner-briefing`,
 * `/pricing`) also hide it so a Google Meet share is not covered by consent UI.
 */
export function shouldShowCookieConsentBanner(opts: {
  showBanner: boolean;
  showModal: boolean;
  /** Truthy when any portal user is signed in. */
  isAuthenticated: boolean;
  location: string;
}): boolean {
  if (!opts.showBanner || opts.showModal || opts.isAuthenticated) {
    return false;
  }

  const { location } = opts;
  if (
    UNLISTED_SCREEN_SHARE_PATHS.has(location) ||
    isPathOrPrefix(location, "/admin") ||
    isPathOrPrefix(location, "/agent") ||
    isPathOrPrefix(location, "/vendor") ||
    isPathOrPrefix(location, "/sponsor") ||
    isPathOrPrefix(location, "/principals") ||
    isPathOrPrefix(location, "/dashboard") ||
    isPathOrPrefix(location, "/deal-room") ||
    isPathOrPrefix(location, "/esign") ||
    location === "/logout"
  ) {
    return false;
  }

  return true;
}

function isPathOrPrefix(location: string, base: string): boolean {
  return location === base || location.startsWith(`${base}/`);
}
